/* eslint-disable import/first */
import {StrictMode} from 'react';
import {createRoot} from 'react-dom/client';
import {I18nextProvider} from 'react-i18next';
import {Provider} from 'react-redux';
import {RouterContextProvider, RouterProvider, createBrowserRouter} from 'react-router';

/* eslint-disable-next-line import/no-extraneous-dependencies */
import isPropValid from '@emotion/is-prop-valid';
import * as Sentry from '@sentry/react';
import {ShouldForwardProp, StyleSheetManager, ThemeProvider} from 'styled-components';

import {storage} from '@edna/utils';

import {App, appRoutes} from 'src/containers/App';
import {ErrorBoundary} from 'src/containers/ServiceUnavailable/RouteErrorBoundary';
import GlobalStyle from 'src/style';

import {Onboarding} from './components';
import {
  EN_DOMAIN_POSTFIX,
  EStorageKeys,
  RU_DOMAIN_POSTFIX,
  isEnDomain,
  isEnvDevelopment,
} from './constants';
import i18n from './i18n';
import {routerContext} from './routerContext';
import store from './store';
import theme from './styles/theme';

if (!isEnvDevelopment) {
  Sentry.setTag('hostname', window.location.hostname);
  Sentry.setTag('tenantId', storage.get(EStorageKeys.TENANT_ID));
  Sentry.setUser({username: String(storage.get(EStorageKeys.USERNAME))});
  Sentry.init({
    dsn: `https://fb85f2ed80a14789b3b21136adb8c47b@sentry.edna${
      isEnDomain() ? EN_DOMAIN_POSTFIX : RU_DOMAIN_POSTFIX
    }/4`,
    autoSessionTracking: true,
    integrations: [new Sentry.BrowserTracing()],
    tracesSampleRate: 1.0,
    normalizeDepth: 10,
  });
}

// This implements the default behavior from styled-components v5
const shouldForwardProp: ShouldForwardProp<'web'> = (propName, target) => {
  if (typeof target === 'string') {
    // For HTML elements, forward the prop if it is a valid HTML attribute
    return isPropValid(propName);
  }

  // For other elements, forward all props
  return true;
};

const RootLayout = () => (
  <Onboarding>
    <App />
  </Onboarding>
);

const router = createBrowserRouter(
  [
    {
      path: '/',
      element: <RootLayout />,
      ErrorBoundary,
      children: appRoutes,
    },
  ],
  {
    getContext() {
      const context = new RouterContextProvider();

      context.set(routerContext, store);

      return context;
    },
  },
);

const Application = () => (
  <StrictMode>
    <Provider store={store}>
      <I18nextProvider i18n={i18n}>
        <StyleSheetManager shouldForwardProp={shouldForwardProp} enableVendorPrefixes>
          <ThemeProvider theme={theme}>
            <GlobalStyle />
            <RouterProvider router={router} />
          </ThemeProvider>
        </StyleSheetManager>
      </I18nextProvider>
    </Provider>
  </StrictMode>
);

const setupBackendMocking = async () => {
  if (!process.env.MOCK_ENDPOINTS?.length) {
    return;
  }

  const {worker} = await import('./jest/mocks/browser');

  return worker.start({
    onUnhandledRequest: 'bypass',
  });
};

const renderApp = () => {
  const target = document.getElementById('root');

  if (!target) {
    console.error(`Root element #root not found`);

    return;
  }

  createRoot(target).render(<Application />);
};

setupBackendMocking().then(renderApp);
/* eslint-enable import/first */
